import React from 'react';
import { Bairro } from '../../types';
import { formatCurrency } from '../../constants';

interface AdminLogisticaProps {
  bairros: Bairro[];
  isUpdating: boolean;
  setIsEditingBairro: (open: boolean) => void;
  setCurrentBairro: (bairro: Partial<Bairro>) => void;
  handleDeleteBairro: (id: string) => void;
  isDemo?: boolean;
}

export const AdminLogistica: React.FC<AdminLogisticaProps> = ({
  bairros,
  isUpdating,
  setIsEditingBairro,
  setCurrentBairro,
  handleDeleteBairro,
  isDemo = false
}) => {
  return (
    <div className="p-4 md:p-10 animate-fade-in space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">Taxas de Entrega</h3>
          <p className="text-slate-400 text-xs md:text-sm font-bold mt-1">Gerencie bairros e custos de frete.</p>
        </div>
        <button 
          onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentBairro({ nome: '', taxa: 0 }); setIsEditingBairro(true); }} 
          className={`w-full sm:w-auto bg-orange-600 text-white px-6 py-4 sm:py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"></path></svg>
          Novo Bairro
        </button>
      </div>
      
      <div className="bg-white rounded-3xl md:rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
        {/* Mobile View: List */}
        <div className="md:hidden divide-y divide-slate-100">
           {bairros.map(b => (
             <div key={b.id} className="p-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <h4 className="font-black text-slate-900 text-sm truncate">{b.nome}</h4>
                    <span className={`inline-block mt-1 px-3 py-1 rounded-lg font-black text-[10px] ${b.taxa === 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-700'}`}>
                        {b.taxa === 0 ? 'Entrega Grátis' : formatCurrency(b.taxa)}
                    </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentBairro(b); setIsEditingBairro(true); }} className="p-2 text-slate-400 bg-slate-50 rounded-xl"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg></button>
                  <button onClick={() => { if(isDemo) return alert("Modo demonstração"); if(confirm('Excluir este bairro?')) handleDeleteBairro(b.id); }} className="p-2 text-slate-400 bg-slate-50 rounded-xl"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                </div>
             </div>
           ))}
        </div>

        {/* Desktop View: Table */}
        <table className="hidden md:table w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Bairro / Região</th>
              <th className="px-10 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Taxa de Entrega</th>
              <th className="px-10 py-5 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bairros.map(b => (
              <tr key={b.id} className="hover:bg-slate-50/50 transition-all group">
                <td className="px-10 py-6"><span className="font-black text-slate-900 text-sm">{b.nome}</span></td>
                <td className="px-10 py-6"><span className={`px-4 py-1.5 rounded-lg font-black text-xs ${b.taxa === 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-700'}`}>{b.taxa === 0 ? 'Grátis' : formatCurrency(b.taxa)}</span></td>
                <td className="px-10 py-6 text-right flex justify-end gap-2">
                  <button onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentBairro(b); setIsEditingBairro(true); }} className={`p-2 text-slate-300 hover:text-orange-600 transition-colors ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg></button>
                  <button onClick={() => { if(isDemo) return alert("Modo demonstração"); if(confirm('Excluir?')) handleDeleteBairro(b.id); }} className={`p-2 text-slate-300 hover:text-red-500 transition-colors ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {bairros.length === 0 && (
          <div className="px-10 py-20 text-center">
            <p className="text-slate-400 font-bold text-sm">Nenhum bairro cadastrado.</p>
          </div>
        )}
      </div>
    </div>
  );
};